import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { useDismiss, useFocusTrap } from '../motion';
import BudgetLedger from './BudgetLedger';
import InvoiceDrawer from './InvoiceDrawer';

/*
 * THE BUDGET MODAL — budget management + the ledger, over the Plan (0116).
 *
 * Vinnie: the Plan had too many inline-expanding panels; only the budget SUMMARY
 * should stay on the Plan, and managing the budget + ledger belongs in its own
 * popup. So the whole surface (total band, edit form, pools, ledger, add) lives
 * here, reusing the app's overlay/modal chrome. Escape or a backdrop click close.
 *
 * INVOICE-ACCEPTANCE GOVERNANCE. Clicking a ledger line opens a right-docked
 * detail drawer beside the modal: the modal re-anchors left (its list stays fully
 * readable) and the drawer slides in over ONE shared scrim. Escape or a backdrop
 * click closes the drawer first, then the modal.
 */

/** Somebody who has asked their system for less movement gets none of this. */
function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
const DRAWER_MS = 260;

export default function BudgetModal({
  startEditing = false,
  focusLineId = null,
  onFocusConsumed,
  onClose,
}: {
  startEditing?: boolean;
  /** A line to open the drawer on as the modal mounts — the Payments tab on
   *  Sign-offs sends the approver straight to the line they must decide. */
  focusLineId?: string | null;
  onFocusConsumed?: () => void;
  onClose: () => void;
}) {
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const { leaving, dismiss } = useDismiss(onClose);
  const panelRef = useRef<HTMLDivElement>(null);

  // The open ledger line — the drawer. Kept here so the modal can re-anchor left
  // and the drawer can be a sibling of the modal over the single shared scrim.
  const [openLineId, setOpenLineId] = useState<string | null>(null);
  const [drawerLeaving, setDrawerLeaving] = useState(false);
  const drawerTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(drawerTimer.current), []);

  // Open the handed-in line once, when the line is really on this board — the deep
  // link from the Payments tab. Guarded so it fires once, then reports it consumed.
  const focused = useRef(false);
  useEffect(() => {
    if (focused.current || !focusLineId) return;
    const exists = (project?.invoices ?? []).some((l) => l.id === focusLineId);
    if (!exists) return;
    focused.current = true;
    setOpenLineId(focusLineId);
    onFocusConsumed?.();
  }, [focusLineId, project, onFocusConsumed]);

  // Only one dialog traps focus at a time (motion.ts): the drawer holds it while
  // it is open, the modal the rest of the time.
  useFocusTrap(panelRef, !openLineId);

  const openLine = useCallback((id: string) => {
    clearTimeout(drawerTimer.current);
    drawerTimer.current = undefined;
    setDrawerLeaving(false);
    setOpenLineId(id);
  }, []);
  const closeDrawer = useCallback(() => {
    if (reducedMotion()) {
      setOpenLineId(null);
      return;
    }
    if (drawerTimer.current) return;
    setDrawerLeaving(true);
    drawerTimer.current = setTimeout(() => {
      drawerTimer.current = undefined;
      setDrawerLeaving(false);
      setOpenLineId(null);
    }, DRAWER_MS);
  }, []);

  // Escape closes the drawer first (the drawer binds its own Escape); the modal's
  // Escape is held back while the drawer is open so one keypress does not close
  // both.
  useEffect(() => {
    if (openLineId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss, openLineId]);

  if (!project) return null;

  const openLine_ = openLineId ? (project.invoices ?? []).find((l) => l.id === openLineId) : undefined;
  // If the open line has gone (removed elsewhere), drop the drawer.
  const showDrawer = !!openLine_;

  return (
    <div
      className={`overlay budget-scene ${leaving ? 'is-leaving' : ''} ${showDrawer ? 'has-drawer' : ''}`}
      onClick={() => (showDrawer ? closeDrawer() : dismiss())}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`modal budget-modal ${leaving ? 'is-leaving' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <span className="modal-ref">
            <span className="modal-head-title">Budget</span>
            <span className="modal-head-dash"> · </span>
            <span className="modal-head-title">{project.name}</span>
          </span>
          <div className="modal-head-actions">
            <button className="modal-close" onClick={dismiss} tabIndex={-1} aria-label="Close (Escape)">
              ESC
            </button>
          </div>
        </div>
        <div className="budget-modal-body">
          <BudgetLedger startEditing={startEditing} openLineId={openLineId} onOpenLine={openLine} />
        </div>
      </div>

      {showDrawer && openLine_ && (
        <InvoiceDrawer projectId={project.id} line={openLine_} leaving={drawerLeaving} onClose={closeDrawer} />
      )}
    </div>
  );
}
